/**
 * Comprehensive Authentication Persistence Utility
 * 
 * Handles token storage, validation, refresh, and recovery scenarios
 * including browser refresh, connection issues, and session management
 */

interface TokenData {
  token: string;
  user: any;
  expiresAt: number;
  refreshToken?: string;
}

interface StorageKeys {
  token: string;
  user: string;
  expiresAt: string;
  refreshToken: string;
  navigation: string;
  features: string;
  permissions: string;
  explicitLogout: string;
}

const STORAGE_KEYS: StorageKeys = {
  token: 'auth_token',
  user: 'auth_user',
  expiresAt: 'auth_expires_at',
  refreshToken: 'auth_refresh_token',
  navigation: 'auth_navigation',
  features: 'auth_features',
  permissions: 'auth_permissions',
  explicitLogout: 'auth_explicit_logout'
};

const LEGACY_KEYS = {
  token: 'token',
  user: 'user',
  navigation: 'navigation',
  features: 'features',
  permissions: 'permissions',
  explicitLogout: 'explicit_logout'
} as const;

class AuthPersistence {
  private isLocalStorage: boolean;
  private refreshPromise: Promise<any> | null = null;

  constructor() {
    this.isLocalStorage = this.detectBestStorage();
  }

  /**
   * Detect the best storage method available
   */
  private detectBestStorage(): boolean {
    try {
      // Test localStorage
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch (e) {
      globalThis.console.warn('localStorage not available, falling back to sessionStorage');
      return false;
    }
  }

  /**
   * Get the current storage method
   */
  private getStorage(): Storage {
    return this.isLocalStorage ? localStorage : sessionStorage;
  }

  private normalizeStoredString(value: string | null): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (lower === 'undefined' || lower === 'null') return null;
    return trimmed;
  }

  private safeJsonParse<T>(raw: string | null): T | null {
    const normalized = this.normalizeStoredString(raw);
    if (!normalized) return null;
    try {
      return JSON.parse(normalized) as T;
    } catch {
      return null;
    }
  }

  /**
   * Store authentication data with expiration tracking
   */
  storeAuthData(token: string, user: any, rememberMe: boolean = false, refreshToken?: string): void {
    try {
      void token;
      void refreshToken;
      this.storeUser(user, rememberMe);
    } catch (error) {
      globalThis.console.error('Failed to store auth data:', error);
      throw new Error('Authentication data could not be stored');
    }
  }

  storeUser(user: any, rememberMe: boolean = false): void {
    try {
      this.isLocalStorage = Boolean(rememberMe);
      const storage = this.getStorage();

      const normalizedUser = user
        ? { ...user, id: user.id || user._id }
        : user;

      if (!normalizedUser || typeof normalizedUser !== 'object') {
        throw new Error('Invalid user payload');
      }

      storage.setItem(STORAGE_KEYS.user, JSON.stringify(normalizedUser));
      storage.removeItem(STORAGE_KEYS.token);
      storage.removeItem(STORAGE_KEYS.expiresAt);
      storage.removeItem(STORAGE_KEYS.refreshToken);

      const otherStorage = this.isLocalStorage ? sessionStorage : localStorage;
      this.clearStorage(otherStorage);
    } catch (error) {
      globalThis.console.error('Failed to store user:', error);
      throw new Error('User data could not be stored');
    }
  }

  getStoredUser(): any | null {
    try {
      if (this.isExplicitLogout()) return null;
      const storages: Storage[] = [localStorage, sessionStorage];
      for (const storage of storages) {
        const userStr = storage.getItem(STORAGE_KEYS.user) || storage.getItem(LEGACY_KEYS.user);
        const parsedUser = this.safeJsonParse<any>(userStr);
        if (parsedUser) {
          return { ...parsedUser, id: parsedUser.id || parsedUser._id };
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Retrieve authentication data with validation
   */
  getAuthData(): TokenData | null {
    try {
      if (this.isExplicitLogout()) {
        return null;
      }

      const user = this.getStoredUser();
      if (!user) return null;

      return {
        token: 'cookie',
        user,
        expiresAt: Number.MAX_SAFE_INTEGER
      };
    } catch (error) {
      globalThis.console.error('Failed to retrieve auth data:', error);
      this.clearAuthData();
      return null;
    }
  }

  /**
   * Get additional auth-related data
   */
  getAuthExtras(): { navigation: any[]; features: string[]; permissions: any } {
    try {
      // CRITICAL FIX: Check both localStorage and sessionStorage for compatibility
      const storages: Storage[] = [localStorage, sessionStorage];
      
      for (const storage of storages) {
        const navigationStr = storage.getItem(STORAGE_KEYS.navigation);
        const featuresStr = storage.getItem(STORAGE_KEYS.features);
        const permissionsStr = storage.getItem(STORAGE_KEYS.permissions);

        const legacyNavigationStr = storage.getItem(LEGACY_KEYS.navigation);
        const legacyFeaturesStr = storage.getItem(LEGACY_KEYS.features);
        const legacyPermissionsStr = storage.getItem(LEGACY_KEYS.permissions);

        if ((!navigationStr && legacyNavigationStr) || (!featuresStr && legacyFeaturesStr) || (!permissionsStr && legacyPermissionsStr)) {
          try {
            const navigation = legacyNavigationStr ? JSON.parse(legacyNavigationStr) : [];
            const features = legacyFeaturesStr ? JSON.parse(legacyFeaturesStr) : [];
            const permissions = legacyPermissionsStr ? JSON.parse(legacyPermissionsStr) : {};

            const storageForExtras = storage;
            storageForExtras.setItem(STORAGE_KEYS.navigation, JSON.stringify(navigation));
            storageForExtras.setItem(STORAGE_KEYS.features, JSON.stringify(features));
            storageForExtras.setItem(STORAGE_KEYS.permissions, JSON.stringify(permissions));

            storage.removeItem(LEGACY_KEYS.navigation);
            storage.removeItem(LEGACY_KEYS.features);
            storage.removeItem(LEGACY_KEYS.permissions);
          } catch (e) {
            storage.removeItem(LEGACY_KEYS.navigation);
            storage.removeItem(LEGACY_KEYS.features);
            storage.removeItem(LEGACY_KEYS.permissions);
          }
        }

        // If we have data in this storage, return it
        if (navigationStr || featuresStr || permissionsStr) {
          return {
            navigation: navigationStr ? JSON.parse(navigationStr) : [],
            features: featuresStr ? JSON.parse(featuresStr) : [],
            permissions: permissionsStr ? JSON.parse(permissionsStr) : {}
          };
        }

        const migratedNavigationStr = storage.getItem(STORAGE_KEYS.navigation);
        const migratedFeaturesStr = storage.getItem(STORAGE_KEYS.features);
        const migratedPermissionsStr = storage.getItem(STORAGE_KEYS.permissions);
        if (migratedNavigationStr || migratedFeaturesStr || migratedPermissionsStr) {
          return {
            navigation: migratedNavigationStr ? JSON.parse(migratedNavigationStr) : [],
            features: migratedFeaturesStr ? JSON.parse(migratedFeaturesStr) : [],
            permissions: migratedPermissionsStr ? JSON.parse(migratedPermissionsStr) : {}
          };
        }
      }

      // Return empty defaults if nothing found
      return { navigation: [], features: [], permissions: {} };
    } catch (error) {
      globalThis.console.error('Failed to retrieve auth extras:', error);
      return { navigation: [], features: [], permissions: {} };
    }
  }

  /**
   * Store additional auth-related data
   */
  storeAuthExtras(navigation: any[] = [], features: string[] = [], permissions: any = {}): void {
    try {
      const storage = this.getStorage();
      
      if (navigation.length > 0) {
        storage.setItem(STORAGE_KEYS.navigation, JSON.stringify(navigation));
      }
      if (features.length > 0) {
        storage.setItem(STORAGE_KEYS.features, JSON.stringify(features));
      }
      if (permissions && Object.keys(permissions).length > 0) {
        storage.setItem(STORAGE_KEYS.permissions, JSON.stringify(permissions));
      }
    } catch (error) {
      globalThis.console.error('Failed to store auth extras:', error);
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    try {
      // Clear both storage methods
      this.clearStorage(localStorage, true);
      this.clearStorage(sessionStorage, true);
      
      globalThis.console.log('Auth data cleared successfully');
    } catch (error) {
      globalThis.console.error('Failed to clear auth data:', error);
    }
  }

  setExplicitLogout(value: boolean): void {
    try {
      const storage = localStorage;
      if (value) {
        storage.setItem(STORAGE_KEYS.explicitLogout, 'true');
      } else {
        storage.removeItem(STORAGE_KEYS.explicitLogout);
      }

      sessionStorage.removeItem(STORAGE_KEYS.explicitLogout);
      sessionStorage.removeItem(LEGACY_KEYS.explicitLogout);
    } catch (error) {
      globalThis.console.error('Failed to set explicit logout flag:', error);
    }
  }

  clearExplicitLogout(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.explicitLogout);
      sessionStorage.removeItem(STORAGE_KEYS.explicitLogout);
      localStorage.removeItem(LEGACY_KEYS.explicitLogout);
      sessionStorage.removeItem(LEGACY_KEYS.explicitLogout);
    } catch (error) {
      globalThis.console.error('Failed to clear explicit logout flag:', error);
    }
  }

  isExplicitLogout(): boolean {
    try {
      const storages: Storage[] = [localStorage, sessionStorage];
      for (const storage of storages) {
        const legacy = storage.getItem(LEGACY_KEYS.explicitLogout);
        const current = storage.getItem(STORAGE_KEYS.explicitLogout);

        if (legacy && legacy === 'true' && !current) {
          localStorage.setItem(STORAGE_KEYS.explicitLogout, 'true');
          storage.removeItem(LEGACY_KEYS.explicitLogout);
          return true;
        }

        if (current === 'true') {
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Clear a specific storage method
   */
  private clearStorage(storage: Storage, preserveExplicitLogout: boolean = false): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      if (preserveExplicitLogout && key === STORAGE_KEYS.explicitLogout) return;
      storage.removeItem(key);
    });

    Object.values(LEGACY_KEYS).forEach(key => {
      if (preserveExplicitLogout && key === LEGACY_KEYS.explicitLogout) return;
      storage.removeItem(key);
    });
  }

  /**
   * Check if token is expired or will expire soon
   */
  isTokenExpiredOrExpiringSoon(bufferMinutes: number = 5): boolean {
    void bufferMinutes;
    return false;
  }

  /**
   * Refresh token with automatic retry and error handling
   */
  async refreshTokenWithRetry(api: any, maxRetries: number = 2): Promise<TokenData | null> {
    void api;
    void maxRetries;
    return this.getAuthData();
  }

  /**
   * Validate current session and refresh if needed
   */
  async validateAndRefreshSession(api: any): Promise<TokenData | null> {
    void api;
    return this.getAuthData();
  }

  /**
   * Get storage info for debugging
   */
  getStorageInfo(): { type: string; hasToken: boolean; expiresAt?: string } {
    const storage = this.getStorage();
    const token = storage.getItem(STORAGE_KEYS.token);
    const expiresAtStr = storage.getItem(STORAGE_KEYS.expiresAt);

    return {
      type: this.isLocalStorage ? 'localStorage' : 'sessionStorage',
      hasToken: !!token,
      expiresAt: expiresAtStr ? new Date(parseInt(expiresAtStr)).toISOString() : undefined
    };
  }
}

// Export singleton instance
export const authPersistence = new AuthPersistence();
export default authPersistence;
